import { type FC, useContext } from "react";

import { isString } from "@huub-web/utils";

import type { TemplateCollection } from "../../types";
import { useTemplateParent } from "../../contexts/TemplateParentContext";
import { PageContentProviderActive } from "../../contexts/PageContentContext";
import { TemplateParser } from "../TemplateParser/TemplateParser";
import { PageContentProvider } from "../../hooks/useAgentPageContent";

/**
 * Shallow-replaces `{id}` placeholders in top-level string values of `data`.
 * Nested objects and arrays (e.g. `templates`, `trailingTemplates`) are left
 * untouched so deeper nesting levels can handle their own `{id}` replacement.
 */
const interpolateData = (
  data: Record<string, unknown> | null | undefined,
  replacer: string,
): Record<string, unknown> | null | undefined => {
  if (!data) return data;

  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      key,
      isString(value) ? value.replace(/\{id\}/g, replacer) : value,
    ]),
  );
};

/**
 * Parses a template collection and renders a TemplateParser for each template.
 * Reads `TemplateParentContext` to replace `{id}` placeholders in template IDs
 * and top-level data strings.
 */
export const TemplatesParser: FC<{
  templates: TemplateCollection;
  level?: number;
}> = ({ templates, level = 0 }) => {
  const parent = useTemplateParent();
  const isInsideProvider = useContext(PageContentProviderActive);
  const idReplacer = parent?.id;

  const rendered = templates?.map((template) => {
    const id =
      idReplacer && isString(template.id)
        ? template.id.replace(/\{id\}/g, idReplacer)
        : template.id;

    const data = idReplacer
      ? interpolateData(template.data, idReplacer)
      : template.data;

    return (
      <TemplateParser
        level={level}
        key={id}
        id={id}
        templateKey={template.templateKey}
        data={data}
      />
    );
  });

  // Wrap with PageContentProvider once at the outermost level.
  // All nested TemplatesParsers (any level) inherit the provider via React context.
  if (!isInsideProvider) {
    return <PageContentProvider>{rendered}</PageContentProvider>;
  }

  return rendered;
};
